import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface CatalogAppOption {
  value: string;
  appId: string;
  name: string;
  bound: boolean;
  spaceLabel: string;
}

function appLabel(app: CatalogAppOption) {
  return `${app.name}（アプリID ${app.appId}）${app.bound ? "（連携済み）" : ""}`;
}

export function CatalogAppCombobox({
  id,
  options,
  value,
  onChange,
}: {
  id: string;
  options: CatalogAppOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [activeValue, setActiveValue] = useState<string | null>(null);
  const selected = options.find((option) => option.value === value);
  const search = (query ?? "").trim().normalize("NFKC").toLocaleLowerCase();
  const filtered = options.filter((option) =>
    `${option.name} ${option.appId}`.normalize("NFKC").toLocaleLowerCase().includes(search),
  );
  const selectable = filtered.filter((option) => !option.bound);
  const active = open ? selectable.find((option) => option.value === activeValue) : undefined;
  const optionId = (option: CatalogAppOption) => `${listId}-${option.value}`;

  useEffect(() => {
    if (active) document.getElementById(`${listId}-${active.value}`)?.scrollIntoView?.({ block: "nearest" });
  }, [active, listId]);

  function select(option: CatalogAppOption) {
    if (option.bound) return;
    onChange(option.value);
    setQuery(null);
    setActiveValue(null);
    setOpen(false);
  }

  return (
    <div
      className="relative"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <Input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={active ? optionId(active) : undefined}
        aria-describedby={`${id}-help`}
        autoComplete="off"
        placeholder="アプリ名・アプリIDで検索、または選択"
        className="pr-10"
        value={query ?? (selected ? appLabel(selected) : "")}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setActiveValue(null);
          setOpen(true);
          onChange("");
        }}
        onKeyDown={(event) => {
          if (event.nativeEvent.isComposing || event.keyCode === 229) return;
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
            const index = open ? selectable.findIndex((option) => option.value === activeValue) : -1;
            const next = event.key === "ArrowDown"
              ? (index + 1) % selectable.length
              : (index < 0 ? selectable.length - 1 : (index - 1 + selectable.length) % selectable.length);
            setActiveValue(selectable[next]?.value ?? null);
          } else if (event.key === "Enter" && open) {
            event.preventDefault();
            if (active) select(active);
          } else if (event.key === "Escape") {
            event.preventDefault();
            setOpen(false);
            setActiveValue(null);
          } else if (event.key === "Tab") {
            setOpen(false);
          }
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        className="absolute right-0 top-0 flex h-10 w-10 items-center justify-center text-muted-foreground"
        aria-label={open ? "アプリの候補を閉じる" : "アプリの候補を表示する"}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          inputRef.current?.focus();
          setOpen(!open);
        }}
      >
        <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </button>
      {open ? (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-input bg-background shadow-md">
          <ul id={listId} role="listbox" aria-label="アプリの候補">
            {filtered.map((option) => (
              <li
                key={option.value}
                id={optionId(option)}
                role="option"
                aria-selected={value === option.value}
                aria-disabled={option.bound}
                data-app-id={option.appId}
                className={cn(
                  "px-3 py-2 text-sm",
                  option.bound ? "cursor-not-allowed text-muted-foreground" : "cursor-pointer hover:bg-accent",
                  active?.value === option.value && "bg-accent",
                )}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => select(option)}
              >
                <span className="block break-words">{appLabel(option)}</span>
                <span className="block break-words text-xs text-muted-foreground">{option.spaceLabel}</span>
              </li>
            ))}
          </ul>
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted-foreground" role="status">
              該当するアプリがありません。
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
